import { ApolloError, useApolloClient } from '@apollo/client'
import { Button } from '@mantine/core'

import { navigate, routes, useLocation, useParams } from '@redwoodjs/router'
import { Metadata, useQuery } from '@redwoodjs/web'

import AuthLogo from 'src/components/DriverAuth/AuthLogo'
import DriverEmbeddedAuth from 'src/components/DriverAuth/DriverEmbeddedAuth'
import ApplicationLoadingScreen from 'src/components/Feedback/ApplicationLoadingScreen/ApplicationLoadingScreen'
import {
  applicationPathForStep,
  forwardQuery,
  LOGIN_ONLY_PARAMS,
} from 'src/lib/driverDeepLink'
import { prefillPhoneFromParam } from 'src/lib/phone.utils'
import Sentry from 'src/lib/sentry'

const CARD_CLASS =
  'w-full max-w-sm min-[601px]:rounded-2xl min-[601px]:bg-white min-[601px]:p-8 min-[601px]:shadow-sm'

const DRIVER_LOGIN_COMPANY_BY_SLUG = gql`
  query DriverLoginCompanyBySlug($slug: String!) {
    publicCompanyBySlug(slug: $slug) {
      companyId
      companyName
      companyLogo
    }
  }
`

const DRIVER_LOGIN_COMPANY_BY_APPLICATION = gql`
  query DriverLoginCompanyByApplication($applicationId: String!) {
    publicCompanyByApplicationId(applicationId: $applicationId) {
      companyId
      companyName
      companyLogo
    }
  }
`

const DRIVER_LOGIN_MY_APPLICATION = gql`
  query DriverLoginMyApplicationForCompany($companyId: String!) {
    myApplicationForCompany(companyId: $companyId)
  }
`

// Legacy: the slug / applicationId could arrive inside redirectTo's own query
// (e.g. redirectTo=/c?slug=dn or /driver-application?applicationId=…). New
// links pass slug directly as ?slug=…; both are supported.
const parseRedirectParam = (
  redirectTo: string | undefined,
  key: string
): string | undefined => {
  if (typeof redirectTo !== 'string') return undefined
  const query = redirectTo.split('?')[1]
  if (!query) return undefined
  return new URLSearchParams(query).get(key) || undefined
}

interface DriverLoginPageProps {
  logoSrc?: string
}

const DriverLoginPage = ({ logoSrc }: DriverLoginPageProps) => {
  const {
    redirectTo,
    applicationId: applicationIdParam,
    phone: phoneParam,
    slug: slugParam,
    s: stepParam,
  } = useParams()
  const { search } = useLocation()
  const client = useApolloClient()
  const companySlug = slugParam || parseRedirectParam(redirectTo, 'slug')
  const applicationId =
    applicationIdParam || parseRedirectParam(redirectTo, 'applicationId')
  const phone = phoneParam || parseRedirectParam(redirectTo, 'phone')

  // Resolve the company from the slug when present, otherwise from the
  // applicationId — so a driver arriving via their application link still gets
  // the branding + jobs board.
  const {
    data: bySlug,
    loading: slugLoading,
    error: slugError,
  } = useQuery(DRIVER_LOGIN_COMPANY_BY_SLUG, {
    variables: { slug: companySlug },
    skip: !companySlug,
  })
  const { data: byApplication, loading: applicationLoading } = useQuery(
    DRIVER_LOGIN_COMPANY_BY_APPLICATION,
    {
      variables: { applicationId },
      skip: !!companySlug || !applicationId,
    }
  )
  const company =
    bySlug?.publicCompanyBySlug ?? byApplication?.publicCompanyByApplicationId
  const companyLoading =
    (!!companySlug && slugLoading) ||
    (!companySlug && !!applicationId && applicationLoading)

  // We can only let a driver verify if there's somewhere to send them after.
  const hasSafeRedirect =
    typeof redirectTo === 'string' && /^\/(?![/\\])/.test(redirectTo)
  const hasDestination = hasSafeRedirect || !!applicationId

  // A slug link lands on /c, which 404s without a resolved company. Distinguish
  // the lookup *failing* (transient — offer a retry) from it succeeding with no
  // such company (a genuinely invalid link). An applicationId already has a
  // valid /driver-application destination, so it proceeds even if the company
  // (branding only) can't be resolved. No context and no redirect means there
  // is nowhere to go.
  const slugLookupFailed = !!companySlug && !!slugError
  let status: 'loading' | 'error' | 'retry' | 'ready'
  if (companyLoading) {
    status = 'loading'
  } else if (slugLookupFailed) {
    status = 'retry'
  } else if (companySlug && !company?.companyId) {
    status = 'error'
  } else if (hasDestination || company?.companyId) {
    status = 'ready'
  } else {
    status = 'error'
  }

  // Where a verified driver lands. A slug link (/c) is resolved here now that
  // we're authenticated — straight to the driver's application (or the short
  // form if they have none), instead of bouncing back to /c?slug=. Any other
  // link honors its explicit redirectTo / applicationId.
  const handleVerified = async ({ e164 }: { e164: string }) => {
    const companyId = company?.companyId
    const shortFormWithParams = () =>
      `${routes.publicShortForm({ companyId })}${forwardQuery(
        search,
        { phone: e164 },
        LOGIN_ONLY_PARAMS
      )}`

    if (companySlug && companyId) {
      try {
        const { data } = await client.query({
          query: DRIVER_LOGIN_MY_APPLICATION,
          variables: { companyId },
          fetchPolicy: 'network-only',
        })
        // A successful query with no application is a genuine new applicant.
        const resolvedApplicationId = data?.myApplicationForCompany ?? null
        navigate(
          resolvedApplicationId
            ? `${applicationPathForStep(stepParam)}${forwardQuery(
                search,
                { applicationId: resolvedApplicationId, phone: e164 },
                LOGIN_ONLY_PARAMS
              )}`
            : shortFormWithParams()
        )
      } catch (error) {
        // A brand-new driver has no user record yet → requireAuth throws
        // UNAUTHENTICATED → they simply need to apply.
        const isNewDriver =
          error instanceof ApolloError &&
          error.graphQLErrors.some(
            (e) => e.extensions?.code === 'UNAUTHENTICATED'
          )
        if (isNewDriver) {
          navigate(shortFormWithParams())
          return
        }
        // Real error (network/server): the session is already established, so
        // re-resolve via /c/{slug} (which retries the lookup with the session)
        // rather than staying on a now-spent OTP that can't be re-verified.
        Sentry.captureException(error)
        navigate(
          `/c/${encodeURIComponent(companySlug)}${forwardQuery(
            search,
            { phone: e164 },
            ['slug', 'redirectTo']
          )}`
        )
      }
      return
    }

    const safeRedirect = hasSafeRedirect ? redirectTo : null
    const destination =
      safeRedirect ||
      (applicationId
        ? `/driver-application?applicationId=${applicationId}`
        : null)
    if (!destination) return
    const [path, query = ''] = destination.split('?')
    navigate(`${path}${forwardQuery(query, { phone: e164 })}`)
  }

  // Brand-new phone (Auth0 signup disabled): send them to the public short form
  // for the company we resolved.
  const handleNewUser = async (e164: string) => {
    const companyId = company?.companyId
    if (!companyId) {
      throw new Error(
        'We could not find your company. Please use your company link.'
      )
    }
    navigate(
      `${routes.publicShortForm({ companyId })}${forwardQuery(
        search,
        { phone: e164 },
        LOGIN_ONLY_PARAMS
      )}`
    )
  }

  // Same loader the router shows for /c and the application, so the hop into
  // /driver-login stays seamless.
  if (status === 'loading') {
    return <ApplicationLoadingScreen />
  }

  return (
    <div className="flex min-h-screen flex-col items-center bg-doubleNickel-gray-50 px-4 pt-16 min-[601px]:justify-center min-[601px]:p-4">
      <Metadata title="Driver Login" description="Log in to your application" />

      {status === 'error' && (
        <div className={CARD_CLASS}>
          <AuthLogo className="mb-6" />
          <div className="text-center">
            <div className="text-lg font-semibold text-doubleNickel-gray-900">
              This link isn&apos;t valid
            </div>
            <div className="mt-2 text-sm text-doubleNickel-gray-600">
              We couldn&apos;t find your company. Please use your company or
              application link to continue.
            </div>
          </div>
        </div>
      )}

      {status === 'retry' && (
        <div className={CARD_CLASS}>
          <AuthLogo className="mb-6" />
          <div className="flex flex-col items-center gap-4 text-center">
            <div>
              <div className="text-lg font-semibold text-doubleNickel-gray-900">
                Something went wrong
              </div>
              <div className="mt-2 text-sm text-doubleNickel-gray-600">
                We couldn&apos;t load this page. Please try again.
              </div>
            </div>
            <Button
              fullWidth
              size="md"
              onClick={() => window.location.reload()}
            >
              Try again
            </Button>
          </div>
        </div>
      )}

      {status === 'ready' && (
        <DriverEmbeddedAuth
          logoSrc={company?.companyLogo || logoSrc}
          companyName={company?.companyName}
          logoLoading={companyLoading}
          seeJobsHref={
            company?.companyId
              ? routes.publicShortForm({ companyId: company.companyId })
              : undefined
          }
          companySlug={companySlug}
          companyId={company?.companyId}
          initialPhone={prefillPhoneFromParam(phone)}
          onVerified={handleVerified}
          onNewUser={handleNewUser}
        />
      )}
    </div>
  )
}

export default DriverLoginPage
